/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

export default makeStyles((theme: Theme) =>
  createStyles({
    landingRoot: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%',
      overflowY: 'auto',
      // The glow behind the prompt card bleeds wider than the card, and on a
      // narrow window that reaches past the page's own padding. Clipping it
      // here is what lets the glow be sized for how it looks rather than for
      // the width it would otherwise add to the page.
      overflowX: 'hidden',
    },
    // The prompt surface, sized to fill the window on its own. Without this
    // the marketing section below would join the same flex distribution and
    // pull the hero up off centre; with it, the first screenful is the prompt
    // and the rest is reached by scrolling.
    heroScreen: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    // Full bleed, and the only inverted surface on the page. That is what
    // makes the scroll out of the prompt screen read as crossing into
    // something else rather than as the page simply carrying on.
    bannerSection: {
      backgroundColor: theme.palette.common.black,
      padding: theme.spacing(8, 3),
      boxSizing: 'border-box',
      textAlign: 'center',
    },
    bannerTitle: {
      color: theme.palette.common.white,
    },
    bannerSubtitle: {
      // Reads as quieter than the title without dropping to a grey that
      // struggles against the dark ground.
      color: theme.palette.grey[200],
      // Keeps the sub-line stacked under the title instead of running the
      // full width of the band on a wide window.
      maxWidth: theme.spacing(70),
      margin: '0 auto',
    },
    valuePropsSection: {
      width: '100%',
      // Wider than the prompt column: this is two columns of content, and at
      // the prompt card's width the diagram and the copy would each be half
      // of a narrow strip.
      maxWidth: theme.spacing(160),
      alignSelf: 'center',
      padding: theme.spacing(10, 3),
      boxSizing: 'border-box',
    },
    landingMain: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4, 3, 8),
      boxSizing: 'border-box',
    },
    landingContent: {
      width: '100%',
      maxWidth: theme.spacing(95),
      display: 'flex',
      flexDirection: 'column',
      // Sits a little above centre so the prompt lands near the optical middle
      // once the hero copy is above it.
      marginBottom: theme.spacing(5),
    },
    prebuiltSection: {
      // Wider than the prompt card above it, so the row of flows reads as
      // spanning more than the prompt does, but held short of the window edges
      // rather than bled into them. Centred by `landingMain`'s `alignItems`.
      width: '80%',
      marginBottom: theme.spacing(6),
    },
    // The caption and the way through to the full catalogue, on one line above
    // the flows. `wrap` rather than a media query: on a narrow window the
    // button drops under the caption instead of squeezing it to one word.
    prebuiltHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
      // On a phone the two have already wrapped onto separate lines, so
      // `space-between` no longer sets one against the other — it just leaves
      // both hanging off the left edge of a centred column. Centred, they stack
      // on the same axis as the strip and the prompt box above it.
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center',
        textAlign: 'center',
      },
    },
    heroTitle: {
      fontWeight: 500,
    },
    exploreIcon: {
      fontSize: theme.spacing(2),
    },
    // Wraps the prompt card so the glow has something to anchor to. The card
    // itself cannot carry it: the gradient has to bleed past the card's edges,
    // and the card clips to its own rounded corners.
    promptCardWrapper: {
      position: 'relative',
      // Makes this a stacking context, which keeps the glow's negative
      // z-index scoped to this box. Without it the glow drops behind the
      // page's own background and disappears.
      isolation: 'isolate',
      '&::before': {
        content: '""',
        position: 'absolute',
        // Reaches well past the card on every side so the falloff finishes in
        // open space rather than being cut into a visible rectangle. The
        // gradient's radii are percentages of this box, so these offsets are
        // the one place the glow's size is set. `landingRoot` clips whatever
        // of this hangs off a narrow window.
        top: theme.spacing(-9),
        bottom: theme.spacing(-9),
        left: theme.spacing(-8),
        right: theme.spacing(-8),
        // Below the card, which is in flow and so paints above any negative
        // z-index sibling.
        zIndex: -1,
        // Same blue as the card's shadow, so the glow reads as that shadow
        // spreading rather than as a second colour on the page.
        background: [
          'radial-gradient(ellipse 60% 75% at 50% 50%',
          'rgba(47,95,167,0.16) 0%',
          'rgba(47,95,167,0.06) 45%',
          'rgba(47,95,167,0) 75%)',
        ].join(', '),
        pointerEvents: 'none',
      },
    },
    // The examples moved out of the prompt box and under it, so they sit
    // outside `promptCardWrapper` — inside it the glow would wash over them.
    examplePromptsSection: {
      marginTop: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    examplePromptsCaption: {
      textAlign: 'center',
    },
    examplePromptsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: theme.spacing(1),
    },
    // A copy of the chip inside `CopilotPromptBox`, not a reference to it:
    // these chips sit on a different page, and restyling the ones in the box
    // should not silently restyle the landing page. Kept visually identical on
    // purpose, so an example reads the same here as it does there.
    examplePromptChip: {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: theme.spacing(2),
      padding: '4px 12px',
      color: theme.palette.secondary.main,
      backgroundColor: 'transparent',
      fontFamily: theme.typography.fontFamily,
      lineHeight: 1.5,
      transition: 'all 0.2s ease',
      minWidth: 0,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:hover': {
        borderColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
        backgroundColor: theme.custom.indigo[100],
      },
    },
    promptCard: {
      // Matches the highlighted prompt card on the create integration page, so
      // the surface a signed in user meets later is recognisably the same one.
      boxShadow: `0px 10px 28px 3px rgba(47,95,167,0.18), 0px 0px 0px 1.5px ${theme.palette.primary.main} !important`,
      '&:hover': {
        boxShadow: `0px 10px 28px 3px rgba(47,95,167,0.3), 0px 0px 0px 1.5px ${theme.palette.primary.main} !important`,
      },
      // The focus indicator for the input inside, carried by the card's own
      // border rather than by a ring around the textarea: a second ring in
      // there reads as a second box sitting inside this one. Thickening the
      // border the card already draws keeps the page at one box while still
      // showing a keyboard visitor where they landed.
      '&:focus-within': {
        boxShadow: `0px 10px 28px 3px rgba(47,95,167,0.3), 0px 0px 0px 3px ${theme.palette.primary.main} !important`,
      },
      transition: 'all 0.3s ease-in-out !important',
    },
  })
);
